import { c as _c } from "react/compiler-runtime";
import { MapPin, Plus, WarningSolid, Xmark } from '@byway/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { type DateValue, parseDate } from '@internationalized/date';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { DialogTrigger } from 'react-aria-components';
import { Controller, type SubmitHandler, type UseFormReturn, useForm } from 'react-hook-form';
import { formatDashedYYYYMMDD } from 'utils/DateFormats';
import { z } from 'zod';
import { Button } from '~/components/Button';
import { Combobox, ComboboxItem, ComboboxItemIcon } from '~/components/Combobox';
import { FlexibleDatesPanel } from '~/components/FlexibleDatesPanel';
import { PlaygroundDateSelector } from '~/components/Playground/PlaygroundDateSelector/PlaygroundDateSelector';
import { Popover } from '~/components/Popover';
import { useIsDesktop } from '~/hooks/useIsDesktop';
import { api } from '~/libs/api';
import type { AllBlueprints } from '~/libs/dato/queries/blueprints';
import { addPricesToBlueprints, type DestinationSearchTerm, sortBlueprintsDefault, useBlueprintIndexStore, useDestinationSearch } from '~/stores/blueprintIndexStore';
import { usePlaygroundActions, usePlaygroundUIState } from '~/stores/playgroundStore';
import type { CMSBlueprint } from '~/types/cms/Blueprint.types';
import { type DestinationNameMaps, getLocalisedDestinationLabel } from '~/utils/blueprintHelpers/destinationLabels';
import styles from './BlueprintIndexSearch.module.css';
export type PricedBlueprint = CMSBlueprint & {
  price: number;
  pricePerNight: number;
  totalPrepayAmountUS: number;
  journeyUniqueId: string;
  startDate: string;
};
// DateValue is what RAC calendar accepts as poss values
const DateValueSchema = z.custom<DateValue>();
const DateAndSearchSchema = z.object({
  destination: z.string().optional(),
  startDate: DateValueSchema.optional(),
  endDate: DateValueSchema.optional()
});
type FormValues = z.infer<typeof DateAndSearchSchema>;
type BlueprintIndexSearchProps = {
  blueprints: AllBlueprints;
  startDate: DateValue | undefined;
  destinationNameMaps: DestinationNameMaps;
};
function BlueprintIndexSearch(props: BlueprintIndexSearchProps) {
  const {
    blueprints,
    startDate: initialStartDate,
    destinationNameMaps
  } = props;
  const locale = useLocale();
  const getBlueprintPricesByDate = api.trip.getBlueprintPricesByDate.useMutation();
  const {
    setStartDate,
    setPricedBlueprints,
    setLoading
  } = useBlueprintIndexStore(state => state);
  const {
    closeDrawer
  } = usePlaygroundActions();
  const {
    isDrawerOpen,
    drawerItem
  } = usePlaygroundUIState();
  const isDesktop = useIsDesktop();
  const formMethods = useForm<FormValues>({
    resolver: zodResolver(DateAndSearchSchema),
    defaultValues: {
      destination: '',
      startDate: initialStartDate,
      endDate: undefined
    }
  });
  const {
    handleSubmit,
    control,
    setError // TODO - display form errors
  } = formMethods;
  const formRef = useRef<HTMLFormElement>(null);
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();

  // updates blueprints when changing dates
  const onSubmit: SubmitHandler<FormValues> = async data => {
    setLoading(true);
    let year, month, day, asatDate;
    try {
      if (data.startDate) {
        ({
          year,
          month,
          day
        } = data.startDate);
        asatDate = formatDashedYYYYMMDD(new Date(year, month - 1, day));
      }
      const res = await getBlueprintPricesByDate.mutateAsync({
        asatDate,
        locale
      });
      const pricedBlueprints = addPricesToBlueprints(res, blueprints) as PricedBlueprint[];
      const sortedPricedBlueprints = sortBlueprintsDefault(pricedBlueprints);
      setPricedBlueprints(sortedPricedBlueprints);
      setStartDate(asatDate);

      // Remove startDate from URL params when date changes
      if (pathname) {
        const params = new URLSearchParams(searchParams?.toString() ?? '');
        params.delete('startDate');
        const query = params.toString();
        const nextUrl = query ? `${pathname}?${query}` : pathname;
        router.replace(nextUrl);
      }
    } catch (err) {
      setError('startDate', {
        type: 'manual',
        message: err.message
      });
    }
    await new Promise(resolve => setTimeout(resolve, 400));
    setLoading(false);
  };
  return <form className={styles.form} ref={formRef} onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.bpiSearchContainer}>
        <DestinationSearch formMethods={formMethods} blueprints={blueprints} destinationNameMaps={destinationNameMaps} />
        <Controller control={control} name="startDate" rules={{
        required: 'Date is required'
      }} render={({
        field: {
          value,
          onChange
        },
        fieldState: {
          error
        }
      }) => {
        return <>
                <DialogTrigger isOpen={isDesktop && isDrawerOpen && drawerItem === 'dates'} onOpenChange={isOpen => {
            if (!isOpen) {
              closeDrawer();
            }
          }}>
                  <div className={styles.datePickerContainer}>
                    <div className={styles.mapsDateSelector}>
                      <PlaygroundDateSelector dataUaClick="bpi_when_click" onChange={async dateData => {
                  if (!dateData) {
                    onChange(undefined);
                    formMethods.setValue('endDate', undefined);
                    setLoading(true);
                    try {
                      const res_0 = await getBlueprintPricesByDate.mutateAsync({
                        asatDate: null,
                        locale
                      });
                      const pricedBlueprints_0 = addPricesToBlueprints(res_0, blueprints) as PricedBlueprint[];
                      const sortedPricedBlueprints_0 = sortBlueprintsDefault(pricedBlueprints_0);
                      setPricedBlueprints(sortedPricedBlueprints_0);
                      setStartDate(null);
                      if (pathname) {
                        const params_0 = new URLSearchParams(searchParams?.toString() ?? '');
                        params_0.delete('startDate');
                        const query_0 = params_0.toString();
                        const nextUrl_0 = query_0 ? `${pathname}?${query_0}` : pathname;
                        router.replace(nextUrl_0);
                      }
                    } catch (err_0) {
                      setError('startDate', {
                        type: 'manual',
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        message: (err_0 as any)?.message ?? 'Error'
                      });
                    } finally {
                      setLoading(false);
                    }
                    return;
                  }
                  if (dateData.type === 'calendar' && typeof dateData.value === 'object') {
                    // Calendar mode with start/end dates
                    const {
                      startDate: newStartDateStr,
                      endDate: newEndDateStr
                    } = dateData.value;

                    // Parse strings to DateValue objects
                    const newStartDate = parseDate(newStartDateStr);
                    const newEndDate = parseDate(newEndDateStr);
                    if (JSON.stringify(newStartDate) !== JSON.stringify(value)) {
                      onChange(newStartDate);
                    }
                    const currentEndDate = formMethods.getValues('endDate');
                    if (JSON.stringify(newEndDate) !== JSON.stringify(currentEndDate)) {
                      formMethods.setValue('endDate', newEndDate);
                    }
                  } else if (dateData.type === 'nights' && typeof dateData.value === 'number') {
                    // Nights mode
                    // Don't supply date so BE can return cheapest dates
                    onChange('');
                  }
                  // Auto-submit form when duration is confirmed
                  setTimeout(() => formRef.current?.requestSubmit(), 0);
                }} />
                    </div>

                    {error && <div className={styles.errorMessage}>
                        <WarningSolid decorative />
                        <p>{error.message}</p>
                      </div>}
                  </div>

                  {isDesktop && <Popover ariaLabel="Edit travel dates" maxWidth={600} offset={25} placement="bottom" isDismissable onOpenChange={isOpen_0 => {
              if (!isOpen_0) {
                closeDrawer();
              }
            }} close noHeader>
                      <FlexibleDatesPanel layout="horizontal" />
                    </Popover>}
                </DialogTrigger>

                <div className={styles.spacing}></div>
              </>;
      }} />
      </div>
    </form>;
}
type DestinationSearchProps = {
  blueprints: AllBlueprints;
  formMethods: UseFormReturn<FormValues>;
  destinationNameMaps: DestinationNameMaps;
};
function DestinationSearch(t0) {
  const $ = _c(16);
  const {
    blueprints,
    formMethods,
    destinationNameMaps
  } = t0;
  const t = useTranslations();
  const [filterValue, setFilterValue] = useState("");
  const {
    destinations,
    setDestinations
  } = useBlueprintIndexStore(_temp);
  const {
    register,
    setValue,
    setFocus
  } = formMethods;
  let t1;
  if ($[0] !== blueprints) {
    t1 = blueprints ?? [];
    $[0] = blueprints;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const searchResults = useDestinationSearch(t1, filterValue);
  let t2;
  if ($[2] !== setFocus) {
    t2 = () => setFocus("destination");
    $[2] = setFocus;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== destinationNameMaps || $[5] !== destinations || $[6] !== filterValue || $[7] !== register || $[8] !== searchResults || $[9] !== setDestinations || $[10] !== setValue || $[11] !== t) {
    t3 = destinations.size === 0 ? <Combobox id="destination-combobox" label={t("navigation.places")} inputValue={filterValue} items={searchResults} data-ua-click="bpi_where_click" onInputChange={text => {
      setFilterValue(text);
    }} placeholder={t("playground.destination_prompt")} border="none" className={styles.combobox} formValue="text" {...register("destination")} shouldFlip={false} placement="bottom" onSelectionChange={id => {
      if (id === "no-results") {
        setValue("destination", "");
        setFilterValue("");
        return;
      }
      const matchedDest = searchResults.find(term => term.id === id);
      if (!matchedDest) {
        return;
      }
      setValue("destination", matchedDest.destination ?? "");
      setFilterValue("");
      setDestinations(new Set([matchedDest.destination ?? ""]));
    }}>{item => {
        if (item.id === "no-results") {
          return <ComboboxItem key="bpi-search-combobox-item-no-results" textValue="no-results"><span>{t("content.feedback.search_dynamic_empty", {
                query: filterValue
              })}</span></ComboboxItem>;
        }
        return <ComboboxItem key={`bpi-search-combobox-item-${item.destination}`} textValue={item.destination} data-ua-click="bpi-destination-select"><div className={styles.pinAndTextContainer}><div className={styles.flex}><ComboboxItemIcon><MapPin decorative={true} size="large" /></ComboboxItemIcon><div><span className={styles.placeName}>{getLocalisedDestinationLabel(item.destination, destinationNameMaps)}</span></div></div><button type="button" aria-label={t("aria.destinations_select")} className={styles.addIcon}><Plus decorative={true} /></button></div></ComboboxItem>;
      }}</Combobox> : <><div className="px-4 py-[18.5px] text-lg font-bold ">{getLocalisedDestinationLabel(destinations.values().next().value ?? "", destinationNameMaps)}</div><Button ariaLabel={t("aria.destinations_clear")} variant="icon" onPress={() => {
        setDestinations(new Set());
        setFilterValue("");
      }}><Xmark size="large" decorative={true} /></Button></>;
    $[4] = destinationNameMaps;
    $[5] = destinations;
    $[6] = filterValue;
    $[7] = register;
    $[8] = searchResults;
    $[9] = setDestinations;
    $[10] = setValue;
    $[11] = t;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== t2 || $[14] !== t3) {
    t4 = <><div className={styles.destinationCombobox} onClick={t2}>{t3}</div></>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}
function _temp(state) {
  return state;
}
type BlueprintIndexLoaderProps = {
  cmsBlueprints: AllBlueprints;
  startDate?: string | null; // asat date - YYYY-MM-DD
  destinationNameMaps: DestinationNameMaps;
};
function BlueprintIndexLoader(t0) {
  const $ = _c(8);
  const {
    cmsBlueprints,
    startDate,
    destinationNameMaps
  } = t0;
  let t1;
  if ($[0] !== cmsBlueprints) {
    t1 = cmsBlueprints ?? [];
    $[0] = cmsBlueprints;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== startDate) {
    t2 = startDate ? parseDate(startDate) : undefined;
    $[2] = startDate;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== destinationNameMaps || $[5] !== t1 || $[6] !== t2) {
    t3 = <div><BlueprintIndexSearch blueprints={t1} startDate={t2} destinationNameMaps={destinationNameMaps} /></div>;
    $[4] = destinationNameMaps;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
export { BlueprintIndexLoader as BlueprintIndexSearch };